import React, { useState } from 'react';
import { Button } from './ui/button';
import { Phone, Menu, X, Globe } from 'lucide-react';

const Header = ({ currentLang, setCurrentLang, translations }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const languages = [
    { code: 'he', name: 'עברית', flag: '🇮🇱' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'en', name: 'English', flag: '🇺🇸' }
  ];

  const currentLangData = languages.find(lang => lang.code === currentLang);

  return (
    <header className="backdrop-blur-md bg-white/80 border-b border-white/20 shadow-lg sticky top-0 z-50 transition-all duration-300">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm sm:text-lg">X</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">XKey24</h1>
              <p className="text-xs text-gray-600 hidden sm:block">{translations[currentLang].tagline}</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            <a href="#services" className="text-gray-700 hover:text-blue-600 transition-colors">
              {translations[currentLang].nav.services}
            </a>
            <a href="#about" className="text-gray-700 hover:text-blue-600 transition-colors">
              {translations[currentLang].nav.about}
            </a>
            <a href="#ourwork" className="text-gray-700 hover:text-blue-600 transition-colors">
              {translations[currentLang].nav.ourWork}
            </a>
            <a href="#areas" className="text-gray-700 hover:text-blue-600 transition-colors">
              {translations[currentLang].nav.areas}
            </a>
            <a href="#contact" className="text-gray-700 hover:text-blue-600 transition-colors">
              {translations[currentLang].nav.contact}
            </a>
          </nav>

          {/* Language Selector & Contact */}
          <div className="hidden lg:flex items-center space-x-4 rtl:space-x-reverse">
            <div className="relative">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center space-x-2 rtl:space-x-reverse px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm hidden xl:inline">{currentLangData?.flag} {currentLangData?.name}</span>
                <span className="text-sm xl:hidden">{currentLangData?.flag}</span>
              </button>
              
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setCurrentLang(lang.code);
                        setIsLangOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3"
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button 
              className="bg-red-600 hover:bg-red-700 text-white flex items-center space-x-1 rtl:space-x-reverse px-2 py-2 text-sm"
              onClick={() => window.open('tel:+972553002025')}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">+972-55-300-2025</span>
              <span className="xl:hidden">Call</span>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 z-50 relative"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto">
            <div className="px-4 py-6">
              <nav className="flex flex-col space-y-6">
                <a 
                  href="#services" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-3 text-lg border-b border-gray-100"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {translations[currentLang].nav.services}
                </a>
                <a 
                  href="#about" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-3 text-lg border-b border-gray-100"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {translations[currentLang].nav.about}
                </a>
                <a 
                  href="#ourwork" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-3 text-lg border-b border-gray-100"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {translations[currentLang].nav.ourWork}
                </a>
                <a 
                  href="#areas" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-3 text-lg border-b border-gray-100"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {translations[currentLang].nav.areas}
                </a>
                <a 
                  href="#contact" 
                  className="text-gray-700 hover:text-blue-600 transition-colors py-3 text-lg border-b border-gray-100"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {translations[currentLang].nav.contact}
                </a>
              
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setCurrentLang(lang.code);
                          setIsMenuOpen(false);
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          currentLang === lang.code 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {lang.flag} {lang.name}
                      </button>
                    ))}
                  </div>
                  
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center space-x-2 rtl:space-x-reverse"
                    onClick={() => window.open('tel:+972553002025')}
                  >
                    <Phone className="w-4 h-4" />
                    <span>+972-55-300-2025</span>
                  </Button>
                </div>
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;